/**
 * Transcript Extraction Types
 * These types represent the structure of transcript extraction data from the student documents API
 */

/**
 * Attribute type that holds values from different sources with confidence levels
 */
export type Attribute<T> = {
  applicantProvidedValue?: T;
  extractedValue?: T | null;
  schoolProvidedValue?: T | null;
  extractedValueConfidence?: 'high' | 'low';
};

/**
 * Document process status enum
 */
export enum DocumentProcessStatus {
  PENDING = 'PENDING',
  DOCUMENT_TYPE_VALIDATION_STARTED = 'DOCUMENT_TYPE_VALIDATION_STARTED',
  DOCUMENT_DATA_EXTRACTION_STARTED = 'DOCUMENT_DATA_EXTRACTION_STARTED',
  DOCUMENT_TYPE_VALIDATION_FAILED = 'DOCUMENT_TYPE_VALIDATION_FAILED',
  DOCUMENT_TYPE_VALIDATION_COMPLETED = 'DOCUMENT_TYPE_VALIDATION_COMPLETED',
  DOCUMENT_DATA_EXTRACTION_FAILED = 'DOCUMENT_DATA_EXTRACTION_FAILED',
  DOCUMENT_DATA_EXTRACTION_COMPLETED = 'DOCUMENT_DATA_EXTRACTION_COMPLETED',
  UPLOADED_NOT_PROCESSED = 'UPLOADED_NOT_PROCESSED',
  UPLOADED_AI_SERVICE_UNAVAILABLE = 'UPLOADED_AI_SERVICE_UNAVAILABLE',
  EXAM_VERIFICATION_FAILED = 'EXAM_VERIFICATION_FAILED',
  EXAM_VERIFICATION_COMPLETED = 'EXAM_VERIFICATION_COMPLETED',
  EXAM_VERIFICATION_MISMATCH = 'EXAM_VERIFICATION_MISMATCH',
  NOT_UPLOADED = 'NOT_UPLOADED',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  COMPLETED = 'DOCUMENT_DATA_EXTRACTION_COMPLETED',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  FAILED = 'DOCUMENT_DATA_EXTRACTION_FAILED',
}

/**
 * High School subject data
 */
export type HighSchoolSubject = {
  subject?: string;
  grade?: string;
  results?: string;
};

/**
 * High School transcript data
 */
export type HighSchoolData = {
  level?: '10' | '11' | '12';
  subjects?: Array<HighSchoolSubject>;
};

/**
 * Post-Secondary course data (course value fields)
 */
export type PostSecondaryCourseValue = {
  courseName?: string;
  courseCode?: string;
  semester?: string;
  credits?: string;
  grade?: string;
  marksObtained?: string;
  results?: string;
  minimum?: string;
  maximum?: string;
};

/**
 * Post-Secondary course (each course is an Attribute with id and manuallyEdited at root)
 */
export type PostSecondaryCourse = Attribute<PostSecondaryCourseValue> & {
  id: string;
  manuallyEdited?: boolean;
  confidence?: 'HIGH' | 'MEDIUM' | 'LOW' | 'FAILED' | 'MANUAL';
};

/**
 * Post-Secondary overall total data
 */
export type OverallTotal = {
  obtained?: string;
  minimum?: string;
  maximum?: string;
  credits?: string;
  gpa?: string;
  grade?: string;
  results?: string;
};

export type GradePoint = {
  educationCourseName: string;
  convertedGradePoint: string;
  equivalentCourseName?: string;
  equivalentCourseCode?: string;
  confidence?: 'HIGH' | 'LOW';
  courseMappingId?: string;
  aiGenerated?: boolean;
};

export type GradeConversion = {
  gradePoints: GradePoint[];
};

/**
 * Post-Secondary transcript data
 */
export type PostSecondaryData = {
  academicLevel?: Attribute<string>;
  studentName?: Attribute<string>;
  instituteName?: Attribute<string>;
  location?: Attribute<string>;
  birthdate?: Attribute<string>;
  degreeEntryDate?: Attribute<string>;
  degreeEndDate?: Attribute<string>;
  level?: Attribute<string>;
  degree?: Attribute<string>;
  fieldOfStudy?: Attribute<string>;
  overallTotal?: OverallTotal;
  courses?: Array<PostSecondaryCourse>;
};

/**
 * Main Transcript Document attributes (from API response)
 */
export type TranscriptDocumentAttributes = {
  documentType: 'TRANSCRIPT';
  contentType?: string;
  description?: string;
  documentProcessStatus?: string;
  fileSize?: number;
  createdAt?: string;
  updatedAt?: string;
  downloadUrl?: string;
  credentialReceived?: Attribute<string>;
  degree?: Attribute<string>;
  fieldOfStudy?: Attribute<string>;
  issuingCountry?: Attribute<string>;
  educationLevel?: Attribute<string>;
  startDate?: Attribute<string>;
  endDate?: Attribute<string>;
  degreeEndDate?: Attribute<string>;
  schoolName?: Attribute<string>;
  courses?: Array<PostSecondaryCourse>;
  overallTotal?: Attribute<OverallTotal>;
  gradeSchemeName?: Attribute<string>;
  gradeSchemeId?: Attribute<string>;
  gradeConversion?: GradeConversion;
  extractedOn?: string;
};

/**
 * Full Transcript Document (API response structure)
 */
export type TranscriptDocument = {
  id: string;
  type: 'transcript';
  attributes: TranscriptDocumentAttributes;
};

/**
 * Field mapping from API field names to display labels (nested structure)
 */
export const TRANSCRIPT_FIELD_LABELS = {
  schoolName: 'Name of Institution',
  educationLevel: 'Education Level',
  issuingCountry: 'Education Country',
  credentialReceived: 'Credential Gained',
  degree: 'Degree',
  fieldOfStudy: 'Field of Study',
  startDate: 'Start Date',
  endDate: 'End Date (or expected)',
  issuedDate: 'Transcript Issued Date',
  gradeSchemeName: 'Grade Scheme',
  overallTotal: {
    gps: 'Overall Total',
    gpa: 'GPA',
    grade: 'Grade',
    obtained: 'Obtained',
    maximum: 'Maximum',
    minimum: 'Minimum',
    credits: 'Credits',
  },
} as const;

/**
 * Helper function to get label from nested path (e.g., "overallTotal.gpa")
 * Supports both flat keys ("schoolName") and nested keys ("overallTotal.gpa")
 */
export function getFieldLabel(path: string): string {
  const parts = path.split('.');
  let value: string | Record<string, unknown> = TRANSCRIPT_FIELD_LABELS;

  for (const part of parts) {
    if (typeof value === 'string') {
      return path; // Can't traverse into a string
    }
    value = value[part] as string | Record<string, unknown>;
    if (value === undefined) {
      return path; // Fallback to path if not found
    }
  }

  return typeof value === 'string' ? value : path;
}

export const ATTRIBUTE_VALUES = ['schoolProvidedValue', 'extractedValue', 'applicantProvidedValue'] as const;
